// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { RectangleEdgeFilter } from '@shared/commons/Graphics';
import { MovingType, ProMath, ToolView } from '../ToolView';
import { SelectionState, ToolSelectionType } from '../Selection';
import { Resources } from '@shared/localizations/Resources';
import { TerceraChartTradingOperation } from '../../Utils/ChartConstants';
import { TradingToolViewBase, TradingToolViewType } from './TradingToolViewBase';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { OffsetModeViewEnum } from '@shared/utils/Trading/OffsetModeViewEnum';
import { OrderToolViewBase } from './OrderToolViewBase';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
export class StopLossLimitToolView extends OrderToolViewBase {
    constructor(order, sltpOrder, getToolViewByOrderId, terceraChartPanelContext, renderer) {
        super();
        this.parentY = -1;
        this.parentValue = NaN;
        this.errorMode = false;
        this.TradeWasModified = false;
        this.priceValue = NaN;
        this.priceY = -1;
        this.lastValidParentValue = NaN;
        this.lastValidPriceValue = NaN;
        this.collapsed = true;
        this.parentOrder = order;
        this.sltpOrder = sltpOrder;
        this.GetToolViewByOrderId = getToolViewByOrderId;
        this.terceraChartPanelContext = terceraChartPanelContext;
        this.myRenderer = renderer;
        if (this.parentOrder != null) {
            const isPosition = !!this.parentOrder.isPosition;
            const ID = isPosition ? this.parentOrder.PositionId : this.parentOrder.OrderNumber;
            const type = isPosition ? TradingToolViewType.PositionToolView : TradingToolViewType.OrderToolView;
            const parentTool = this.GetToolViewByOrderId(ID, type);
            if (parentTool != null) {
                this.collapsed = parentTool.Collapsed;
            }
        }
        this.movingType = MovingType.OnlyY;
        this.ToolViewType = TradingToolViewType.StopLossLimitToolView;
        this.Updated();
    }
    AllowCancelOrder() {
        return IsAllowed.IsOrderCancelingAllowed(this.sltpOrder).Allowed;
    }
    AllowTS() {
        if (this.parentOrder.IsActivated) {
            return false;
        } // comment in #109958
        return IsAllowed.IsTradingAllowed(this.parentOrder.Account, this.parentOrder.Instrument, OrderType.TrailingStop)
            .Allowed;
    }
    AllowModify() {
        return ((IsAllowed.IsOrderModifyingAllowed(this.sltpOrder).Allowed ||
            (this.sltpOrder === null && IsAllowed.IsOrderModifyingAllowed(this.parentOrder).Allowed)) &&
            IsAllowed.IsOrderSLTPAllowed(this.parentOrder).Allowed);
    }
    get Collapsed() {
        return this.collapsed;
    }
    set Collapsed(value) {
        if (this.collapsed != value) {
            this.collapsed = value;
            if (this.parentOrder != null) {
                const isPosition = !!this.parentOrder.isPosition;
                const ID = isPosition ? this.parentOrder.PositionId : this.parentOrder.OrderNumber;
                const type = this.myRenderer.getToolViewTypeByOrder(this.parentOrder);
                const parentTool = this.GetToolViewByOrderId(ID, type);
                parentTool.Collapsed = this.collapsed;
            }
        }
    }
    get ID() {
        return this.parentOrder.OrderNumber + '-SLL';
    }
    get Price() {
        const order = this.parentOrder;
        if ((order === null || order === void 0 ? void 0 : order.isPosition) && this.sltpOrder) {
            return this.sltpOrder.Price;
        }
        return order ? order.GetStopLossLimitInPriceValue() : NaN;
    }
    get Side() {
        if (this.sltpOrder != null) {
            return this.sltpOrder.BuySell;
        }
        return this.parentOrder.BuySell === OperationType.Buy ? OperationType.Sell : OperationType.Buy;
    }
    Updated() {
        let parentOrder = this.parentOrder;
        const isPositionSL = parentOrder === null || parentOrder === void 0 ? void 0 : parentOrder.isPosition;
        if (isPositionSL) {
            parentOrder = this.sltpOrder;
        }
        if (!parentOrder)
            return;
        const noStopLoss = isPositionSL ? parentOrder.Price === null : isNaN(parentOrder.StopLossPriceValue);
        if (noStopLoss) {
            return;
        }
        this.dataCacheTool.Points[0][0] = parentOrder.UTCDateTime;
        this.dataCacheTool.Points[0][1] = this.Price;
        if (!this.CurrentMovement.IsMovingRightNow) {
            this.CancelMoving();
        }
    }
    isTrailingStop() {
        return false;
    }
    CancelMoving() {
        this.TradeWasModified = false;
        this.ResetMove();
        this.ResetMouse();
    }
    Draw(gr, window, param) {
        this.MainOrderVisibility = false;
        if (!this.myRenderer.ShowOrders) {
            return;
        }
        if (!isNullOrUndefined(this.parentOrder) &&
            (!isValidNumber(this.parentOrder.Price) || this.parentOrder.Price === 0)) {
            return;
        }
        if (!isNullOrUndefined(this.sltpOrder) && this.sltpOrder.OrderType === OrderType.TrailingStop) {
            return;
        } // #93794
        try {
            const CR = window.ClientRectangle;
            const minX = CR.X;
            const maxX = minX + CR.Width;
            let screenPoints00 = this.screenPoints[0][0] < minX ? minX : this.screenPoints[0][0];
            const offsetX = minX +
                (this.parentOrder.OrderType === OrderType.StopLimit
                    ? this.getPlateOffset() * 3
                    : this.getPlateOffset() * 2);
            if (this.myRenderer.ShowVisualTradingOnLeftSide) {
                screenPoints00 = offsetX;
            }
            else {
                screenPoints00 += offsetX;
            }
            let screenPoints01 = this.screenPoints[0][1];
            if (!isNaN(this.priceValue)) {
                screenPoints01 = Math.floor(this.priceY);
            }
            // Ловим ошибки
            if ((screenPoints00 < 0 && maxX < 0) ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 <= ProMath.infinityMinus ||
                screenPoints01 <= ProMath.infinityMinus) {
                return;
            }
            const separators = [];
            const curX0 = screenPoints00;
            let curX = curX0;
            const curY = screenPoints01 - this.containerHeight / 2;
            this.MainOrderVisibility = true;
            const hoveredOrSelected = this.CurrentSelection.CurrentState !== SelectionState.None;
            // оптимизация - вне не рисуем ордер и не добавляем его плашку
            const orderIsBuy = this.Side === OperationType.Buy;
            const imageText = 'SLL';
            let shortDescriptionText = '';
            let trailingButtonText = '';
            if (CR.Contains(screenPoints00, screenPoints01)) {
                const imageTextPad = 2;
                const imageRectWidth = Math.floor(gr.GetTextWidth(imageText, TradingToolViewBase.tradeFont)) + imageTextPad * 2;
                this.imageRect = new Rectangle(curX, curY, imageRectWidth, this.containerHeight);
                curX += this.imageRect.Width;
                if (!this.Collapsed) {
                    shortDescriptionText = this.DescriptionText();
                    curX = this.addTextRectWithSeparator(gr, shortDescriptionText, curX, curY, separators, (r) => {
                        this.infoRect = r;
                    });
                    if (!this.errorMode) {
                        const isActivated = !isNullOrUndefined(this.sltpOrder) ? this.sltpOrder.IsActivated : false;
                        // Trailing stop btn for activated order's sll
                        if (isActivated && this.AllowTS() && !TradingLockUtils.TradingLock.tradingLocked) {
                            trailingButtonText = 'TSL';
                            curX = this.addTextRectWithSeparator(gr, trailingButtonText, curX, curY, separators, (r) => {
                                this.trailingButton = r;
                            });
                        }
                        else {
                            this.trailingButton = Rectangle.Empty();
                        }
                        // Remove btn for activated order's sll
                        if (isActivated &&
                            this.AllowCancelOrder() &&
                            !TradingLockUtils.TradingLock.tradingLocked &&
                            !param.TerceraChart.TerceraChartTradingToolsRenderer.NotTradingContext) {
                            curX = this.addRectWithSeparator(curX, curY, this.btnSize, separators, (r) => {
                                this.removeButton = r;
                            });
                        }
                        else {
                            this.removeButton = Rectangle.Empty();
                        }
                    }
                }
                else {
                    this.infoRect = Rectangle.Empty();
                    this.removeButton = Rectangle.Empty();
                    this.actionButton = Rectangle.Empty();
                    this.trailingButton = Rectangle.Empty();
                }
            }
            else {
                this.imageRect = Rectangle.Empty();
            }
            this.mainContainer = new Rectangle(curX0, curY, curX - curX0, this.containerHeight);
            const { backgroundPen, backgroundBrush, TextBrush } = this.getDrawingTools(orderIsBuy, hoveredOrSelected);
            gr.RoundRectangle(this.mainContainer, 4, backgroundBrush);
            this.drawCenteredText(gr, imageText, this.imageRect, TradingToolViewBase.tradeFont, TextBrush);
            this.drawCenteredText(gr, shortDescriptionText, this.infoRect, TradingToolViewBase.tradeFont, TextBrush);
            const trailingBrush = this.getButtonBrush(orderIsBuy, ToolSelectionType.Trailing === this.CurrentSelection.CurrentHover);
            this.drawTextButton(gr, this.trailingButton, trailingButtonText, TradingToolViewBase.tradeFont, TextBrush, trailingBrush);
            const removeBrush = this.getButtonBrush(orderIsBuy, ToolSelectionType.Remove === this.CurrentSelection.CurrentHover);
            this.drawImgButton(gr, this.removeButton, ThemeManager.NewImages.closeIconDefault, ThemeManager.NewImages.closeIconHovered, removeBrush, RectangleEdgeFilter.All & (RectangleEdgeFilter.BottomRight | RectangleEdgeFilter.TopRight));
            this.drawSeparators(gr, separators);
            const priceLinePen = this.getPriceLinePen(orderIsBuy);
            this.drawPriceLine(gr, this.mainContainer, maxX, screenPoints01, priceLinePen);
            const priceBrush = this.getPricePlateBrush(orderIsBuy);
            this.tryAddPlateOnPriceScale(param, this.priceValue, this.Price, priceBrush);
            // Bind line
            this.drawBindLines(gr, hoveredOrSelected, orderIsBuy, screenPoints01);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
    }
    UpdateScreenPoints(window, cashItemSeries = null) {
        super.UpdateScreenPoints(window, cashItemSeries);
        if (isNaN(this.parentValue) && isNaN(this.lastValidParentValue)) {
            // для рисования линии связи
            let price;
            if (this.parentOrder.isPosition) {
                const position = this.parentOrder;
                if (!position.IsActivated) {
                    price = position.SLOrder ? position.SLOrder.StopLimit : position.Price;
                }
                else {
                    // #93540
                    price = position.Price;
                }
            }
            else {
                price = this.parentOrder.GetStopLossInPriceValue();
            }
            this.parentY = Math.floor(Math.round(window.PointsConverter.GetScreenY(price)));
            if (!isNullOrUndefined(cashItemSeries)) {
                this.parentY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(price))));
            }
        }
    }
    // #region mouse
    /// <summary>
    /// Является ли тулза выделенной мышью (сейчас по линиям, но можно и весь полигон)
    /// </summary>
    IsSelectCheck(x, y) {
        if (this.TradeWasModified) {
            // тулза смещена и с сервера не пришло подтверждение не двигаем её
            return false;
        }
        if (!this.myRenderer.ShowOrders) {
            return false;
        }
        const newSelection = this.GetCurrentSelection(x, y);
        return newSelection !== ToolSelectionType.None;
    }
    GetCurrentSelection(x, y) {
        let newSelection = ToolSelectionType.None;
        if (this.imageRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Image;
        }
        else if (this.actionButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Action;
        }
        else if (this.removeButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Remove;
        }
        else if (this.infoRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Info;
        }
        else if (this.trailingButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Trailing;
        }
        else if (Math.abs(y - this.screenPoints[0][1]) < ToolView.POINT_DX) {
            newSelection = ToolSelectionType.Line;
        }
        return newSelection;
    }
    CheckMinDistanceForStartMoving(x, y) {
        const mouseDownHover = this.CurrentSelection.MouseDownHover;
        if (this.AllowModify() &&
            !TradingLockUtils.TradingLock.tradingLocked &&
            (mouseDownHover === ToolSelectionType.Image || mouseDownHover === ToolSelectionType.Info)) {
            return Math.abs(this.CurrentMovement.PrevY - y) >= this.MinDistanceForStartMoving();
        }
        return false;
    }
    // move
    ProcessNewPosition(window, x, y, cashItemSeries = null) {
        this.TradeWasModified = true;
        // move order
        if (this.AllowModify()) {
            super.ProcessNewPosition(window, x, y, cashItemSeries); // обновляем главного
            this.priceValue = window.PointsConverter.GetDataY(y);
            if (!isNullOrUndefined(cashItemSeries)) {
                this.priceValue = cashItemSeries.settings.RevertConverter(window.PointsConverter.GetDataY(y));
            }
            this.priceY = y;
            if (this.parentOrder.isPosition) {
                // const prVal = this.myRenderer.Instrument.roundPrice(this.priceValue);
                // this.errorMode = this.parentOrder.BuySell == OperationType.Buy ? prVal > this.parentOrder.CurPriceClose : prVal < this.parentOrder.CurPriceClose;
            }
            else {
                const parentPrice = this.parentOrder.Price;
                const prVal = this.myRenderer.Instrument.roundPrice(this.priceValue);
                // this.errorMode = this.parentOrder.BuySell == OperationType.Buy ? prVal >= parentPrice : prVal <= parentPrice;
            }
        }
    }
    // mouse finish
    ProcessMoveFinish() {
        try {
            super.ProcessMoveFinish();
            if (this.errorMode) {
                this.ResetMove();
                this.Updated();
                return;
            }
            const mouseDownHover = this.CurrentSelection.MouseDownHover;
            if (mouseDownHover === ToolSelectionType.Image ||
                mouseDownHover === ToolSelectionType.Info ||
                mouseDownHover === ToolSelectionType.Line) {
                const ins = this.myRenderer.Instrument;
                const newPrice = ins ? ins.roundPrice(this.priceValue) : this.priceValue;
                const trailing = this.isTrailingStop();
                let delta = newPrice;
                this.lastValidPriceValue = newPrice;
                if (trailing) {
                    // const parentPrice = this.parentOrder.isPosition ? this.parentOrder.CurPriceClose : this.parentOrder.Price;
                    delta = ins ? ins.roundPrice(delta) : delta;
                }
                this.terceraChartPanelContext.ChartVisualTrading(this.parentOrder, {
                    action: TerceraChartTradingOperation.ModifySLL,
                    price: delta,
                    ts: trailing,
                    CancelCallback: this.undoChanges.bind(this)
                });
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
        finally {
            this.ResetMouse();
        }
    }
    // mouse click
    ProcessClick() {
        if (this.CurrentSelection.MouseDownHover === this.CurrentSelection.CurrentHover) {
            switch (this.CurrentSelection.MouseDownHover) {
                case ToolSelectionType.Image:
                case ToolSelectionType.Info:
                    this.Collapsed = !this.Collapsed;
                    break;
                case ToolSelectionType.Action:
                    if (!TradingLockUtils.TradingLock.tradingLocked) {
                        this.terceraChartPanelContext.ChartVisualTrading(this.sltpOrder, {
                            action: TerceraChartTradingOperation.ActionButton
                        });
                    }
                    break;
                case ToolSelectionType.Remove:
                    if (!TradingLockUtils.TradingLock.tradingLocked) {
                        this.terceraChartPanelContext.ChartVisualTrading(this.parentOrder, {
                            action: TerceraChartTradingOperation.RemoveSL
                        });
                    }
                    break;
                case ToolSelectionType.Trailing:
                    if (!TradingLockUtils.TradingLock.tradingLocked) {
                        const trailing = !this.isTrailingStop();
                        // const parentPrice = this.parentOrder.isPosition ? this.parentOrder.CurPriceClose : this.parentOrder.Price;
                        let delta = this.Price;
                        delta =
                            this.myRenderer.Instrument != null ? this.myRenderer.Instrument.roundPrice(delta) : delta;
                        this.terceraChartPanelContext.ChartVisualTrading(this.parentOrder, {
                            action: TerceraChartTradingOperation.ModifySLL,
                            ts: trailing,
                            price: delta
                        });
                    }
                    break;
                case ToolSelectionType.Line:
                case ToolSelectionType.None:
                default:
                    break;
            }
        }
        this.ResetMouse();
    }
    ResetMouse() {
        this.CurrentSelection.MouseDownHover = ToolSelectionType.None;
        this.errorMode = false;
        this.priceValue = NaN;
        if (isNaN(this.lastValidPriceValue)) {
            this.priceY = NaN;
        }
    }
    ResetMove() {
        this.lastValidPriceValue = NaN;
        this.lastValidParentValue = NaN;
        this.priceY = -1;
        this.TradeWasModified = false;
    }
    // TODO. Rename. Refactor.
    undoChanges() {
        this.ResetMove();
        this.Updated();
    }
    GetCursor(e) {
        const curElement = this.CurrentSelection.MouseDownHover === ToolSelectionType.None
            ? this.CurrentSelection.CurrentHover
            : this.CurrentSelection.MouseDownHover;
        switch (curElement) {
            case ToolSelectionType.Image:
            case ToolSelectionType.Info:
                return this.AllowModify() ? (this.errorMode ? Cursors.No : Cursors.NoMoveVert) : null;
            case ToolSelectionType.Action:
            case ToolSelectionType.Remove:
            case ToolSelectionType.Line:
            case ToolSelectionType.None:
            case ToolSelectionType.Trailing:
            default:
                return null;
        }
    }
    GetTooltip(e) {
        let res = '';
        switch (this.CurrentSelection.CurrentHover) {
            case ToolSelectionType.Image:
                res = Resources.getResource('TradeTool.Order');
                break;
            case ToolSelectionType.Info:
                res = Resources.getResource('TradeTool.Tick and P/L');
                break;
            case ToolSelectionType.Remove:
                res = Resources.getResource('TradeTool.Cancel order');
                break;
            default:
                res = null;
                break;
        }
        return res ? super.GetTooltip(e) : res;
    }
    // #endregion
    Dispose() {
        this.GetToolViewByOrderId = null;
        this.parentOrder = null;
        this.sltpOrder = null;
        super.Dispose();
    }
    DescriptionText() {
        // от цены открытия.
        let orderPrice = this.parentOrder.Price;
        if (!isNaN(this.parentValue)) {
            orderPrice = this.parentValue;
        }
        else if (!isNaN(this.lastValidParentValue)) // #49010 - здесь восстанавливаем значение цены, мышку мы отпустили и parentValue == NaN, но ордер еще не выставился
         {
            orderPrice = this.lastValidParentValue;
        }
        let curPrice = this.priceValue;
        if (!isNaN(this.priceValue)) {
            this.lastValidPriceValue = this.priceValue;
        }
        if (isNaN(this.priceValue) && !isNaN(this.lastValidPriceValue)) {
            curPrice = this.lastValidPriceValue;
        }
        else {
            curPrice = this.priceValue;
        }
        const ins = this.myRenderer.instrument;
        const revertedPrice = curPrice;
        const price = !isNaN(revertedPrice) ? revertedPrice : this.Price;
        const offsetType = BaseSettings.offsetType;
        const offsetValue = offsetType === OffsetModeViewEnum.Percent
            ? SlTpUtils.calculatePercent(price, orderPrice)
            : SlTpUtils.calculateTicks(price, orderPrice, ins);
        const offsetFormattedValue = SlTpUtils.formatSLTPValue(offsetValue, ins, offsetType);
        let pnl = '';
        pnl = this.parentOrder.Account.formatPrice(TradingToolViewBase.FuturesSlTp(this.parentOrder, price - orderPrice));
        let shortDescriptionText = offsetFormattedValue + SlTpUtils.getSymbolForOffsetMode(offsetType);
        if (this.errorMode) {
            pnl = '';
            shortDescriptionText = Resources.getResource('chart.visualTrading.Invalid price');
        }
        return pnl + '  ' + shortDescriptionText;
    }
    getPriceLinePen(isBuy) {
        if (!isBuy) {
            return this.TradeWasModified ? TradingToolViewBase.chartOrderModifiedPen : this.myRenderer.orderSellPen;
        }
        else {
            return this.TradeWasModified ? TradingToolViewBase.chartOrderModifiedPen : this.myRenderer.orderBuyPen;
        }
    }
    drawBindLines(gr, hoveredOrSelected, orderIsBuy, curY0) {
        if (this.Collapsed && !hoveredOrSelected) {
            return;
        }
        const pen = this.getPriceLinePen(orderIsBuy);
        if (this.parentY > curY0) {
            const verticalX = this.imageRect.X - this.indentationLineLength;
            gr.DrawLine(pen, verticalX, this.imageRect.Y + this.imageRect.Height / 2, verticalX, this.parentY - this.imageRect.Height / 2);
            gr.DrawLine(pen, verticalX, this.imageRect.Y + this.imageRect.Height / 2, this.imageRect.X, this.imageRect.Y + this.imageRect.Height / 2);
        }
        else {
            const verticalX = this.imageRect.X - this.indentationLineLength;
            gr.DrawLine(pen, verticalX, this.imageRect.Y + this.imageRect.Height / 2, verticalX, this.parentY + this.imageRect.Height / 2);
            gr.DrawLine(pen, verticalX, this.imageRect.Y + this.imageRect.Height / 2, this.imageRect.X, this.imageRect.Y + this.imageRect.Height / 2);
        }
    }
}
